import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { LIST_MEDIA_AGENT_POOL_QUERY_KEY } from '@/state/queries/media-agent-admin/use-list-media-agent-pool';

export type LifecycleForceBody = {
  force?: boolean;
  reason?: string;
};

export type CapacityPreflightBlocker = {
  code: string;
  message: string;
  details?: Record<string, unknown>;
};

export type CapacityPreflightConflict = {
  status: 409;
  message: string;
  blockers: CapacityPreflightBlocker[];
  impact?: Record<string, unknown>;
};

export class MediaAgentLifecycleConflictError extends Error {
  readonly status = 409;
  readonly blockers: CapacityPreflightBlocker[];
  readonly impact?: Record<string, unknown>;

  constructor(payload: CapacityPreflightConflict) {
    super(payload.message);
    this.name = 'MediaAgentLifecycleConflictError';
    this.blockers = payload.blockers;
    this.impact = payload.impact;
  }
}

function extractConflictPayload(body: Record<string, unknown> | undefined): {
  blockers: CapacityPreflightBlocker[];
  impact?: Record<string, unknown>;
  message?: string;
} | null {
  if (!body) return null;

  const details =
    body.details && typeof body.details === 'object' ? (body.details as Record<string, unknown>) : undefined;

  const blockers = Array.isArray(body.blockers)
    ? body.blockers
    : Array.isArray(details?.blockers)
      ? details.blockers
      : null;
  if (!blockers) return null;

  const impact =
    (body.impact as Record<string, unknown> | undefined) ?? (details?.impact as Record<string, unknown> | undefined);

  return {
    blockers: blockers as CapacityPreflightBlocker[],
    impact,
    message: typeof body.message === 'string' ? body.message : undefined,
  };
}

async function readJsonBody(res: Response): Promise<Record<string, unknown> | undefined> {
  const text = await res.text();
  if (!text.trim()) {
    return undefined;
  }
  try {
    return JSON.parse(text) as Record<string, unknown>;
  } catch {
    return undefined;
  }
}

export async function parseLifecycleResponse(res: Response, action: string): Promise<unknown> {
  const body = await readJsonBody(res);

  if (res.ok) {
    return body ?? {};
  }

  if (res.status === 409) {
    const conflict = extractConflictPayload(body);
    if (conflict) {
      throw new MediaAgentLifecycleConflictError({
        status: 409,
        message: conflict.message ?? `Media agent action failed (${action})`,
        blockers: conflict.blockers,
        impact: conflict.impact,
      });
    }
  }

  throw new Error(typeof body?.message === 'string' ? body.message : `Media agent action failed (${action})`);
}

export function useCreateAzureMediaAgentMutation() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body?: { name?: string }) => {
      const token = (await getToken()) ?? '';
      const url = `${import.meta.env.VITE_API_URL}/media-agent/players/azure`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body ?? {}),
      });
      if (!res.ok) throw new Error('Failed to create Azure media agent');
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [LIST_MEDIA_AGENT_POOL_QUERY_KEY] }),
  });
}

export function useCreateStandaloneMediaAgentMutation() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body?: { name?: string }) => {
      const token = (await getToken()) ?? '';
      const url = `${import.meta.env.VITE_API_URL}/media-agent/players/standalone`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body ?? {}),
      });
      if (!res.ok) throw new Error('Failed to create standalone media agent');
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [LIST_MEDIA_AGENT_POOL_QUERY_KEY] }),
  });
}

export function useMediaAgentLifecycleMutation(path: 'maintenance' | 'pause' | 'resume' | 'delete') {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: string | { playerId: string; body?: LifecycleForceBody }) => {
      const playerId = typeof input === 'string' ? input : input.playerId;
      const body = typeof input === 'string' ? undefined : input.body;
      const token = (await getToken()) ?? '';
      const method = path === 'delete' ? 'DELETE' : 'POST';
      const url =
        path === 'delete'
          ? `${import.meta.env.VITE_API_URL}/media-agent/players/${encodeURIComponent(playerId)}`
          : `${import.meta.env.VITE_API_URL}/media-agent/players/${encodeURIComponent(playerId)}/${path}`;

      const res = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          ...(path === 'maintenance' || path === 'pause' ? { 'Content-Type': 'application/json' } : {}),
        },
        body: path === 'maintenance' || path === 'pause' ? JSON.stringify(body ?? {}) : undefined,
      });
      return parseLifecycleResponse(res, path);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [LIST_MEDIA_AGENT_POOL_QUERY_KEY] }),
  });
}

export function useScheduleMediaAgentMaintenanceMutation() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      playerId: string;
      startsAt: string;
      endsAt: string;
      force?: boolean;
      reason?: string;
    }) => {
      const token = (await getToken()) ?? '';
      const url = `${import.meta.env.VITE_API_URL}/media-agent/players/${encodeURIComponent(input.playerId)}/schedule-maintenance`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          startsAt: input.startsAt,
          endsAt: input.endsAt,
          force: input.force,
          reason: input.reason,
        }),
      });
      return parseLifecycleResponse(res, 'schedule-maintenance');
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [LIST_MEDIA_AGENT_POOL_QUERY_KEY] }),
  });
}

export function useCancelScheduledMediaAgentMaintenanceMutation() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (playerId: string) => {
      const token = (await getToken()) ?? '';
      const url = `${import.meta.env.VITE_API_URL}/media-agent/players/${encodeURIComponent(playerId)}/schedule-maintenance`;
      const res = await fetch(url, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      return parseLifecycleResponse(res, 'cancel-scheduled-maintenance');
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [LIST_MEDIA_AGENT_POOL_QUERY_KEY] }),
  });
}
